import { ClipboardIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useEffect, useState, type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { cn } from '@/lib/utils.js';

import { notify } from '@/lib/notify.js';

import { useCredentialAction } from '../../hooks/useCredentialAction.js';
import * as api from '../../services/integration-api.js';

import { ConnectionChoiceSelect } from './ConnectionChoiceSelect.js';
import { useConnectorScope } from './connector-scope.js';

interface FormErrors {
  pat?: string;
  organization?: string;
  submit?: string;
}

/**
 * Azure DevOps connect, two screens on each path. The token path asks for the
 * PAT, then the organization — picked from the list of organizations the token
 * can reach, or typed manually when the token is org-scoped (ADO's discovery
 * API answers 401 for those, so there is nothing to list). The sign-in path
 * runs its popup in the connection-method dialog and opens this form on the
 * organization alone.
 *
 * The organization step is mandatory on BOTH paths: every connection pins one,
 * there is no deployment default, and a connection without one is unusable.
 */
export function AzureDevOpsCredentialsForm({
  onSaved,
  onCancel,
  details,
  explainUnavailableActions = false,
  onBusyChange,
  afterSignIn = false,
}: {
  onSaved: () => void;
  onCancel: () => void;
  /**
   * What the new connection should be called and when the agent should reach for it, plus which lane it
   * lands in. Absent on a first connection of the provider — there is nothing to tell it apart from.
   */
  details?: api.ConnectionCreateDetails;
  explainUnavailableActions?: boolean;
  onBusyChange?: (busy: boolean) => void;
  /**
   * Whether the OAuth popup has already run. The connection exists by then, so the form skips the
   * token entirely and asks only for the organization to pin on it.
   */
  afterSignIn?: boolean;
}): JSX.Element {
  const [step, setStep] = useState<'token' | 'org' | 'oauth-org'>(
    afterSignIn ? 'oauth-org' : 'token',
  );
  const [pat, setPat] = useState('');
  const [organization, setOrganization] = useState('');
  const [organizations, setOrganizations] = useState<string[] | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [discovering, setDiscovering] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPat, setShowPat] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const { workspaceId } = useConnectorScope();
  const action = useCredentialAction(
    explainUnavailableActions,
    `${workspaceId ?? ''}:${details?.connectionId ?? 'new'}`,
    onBusyChange,
  );

  /**
   * Lists the organizations the signed-in account can reach, so the step that follows the popup offers
   * them instead of asking for a name. A failed or empty listing leaves the manual field — the account
   * may only be able to see its organizations with a token scoped to list them.
   */
  useEffect(() => {
    if (!afterSignIn) return;
    let current = true;
    const loadOrganizations = async (): Promise<void> => {
      const found = await api
        .listMyAzureDevOpsOrganizations(workspaceId)
        .catch(() => null);
      if (!current) return;
      setOrganizations(found && found.length > 0 ? found : null);
      setOrganization(found?.[0] ?? '');
    };
    void loadOrganizations();
    return () => {
      current = false;
    };
  }, [afterSignIn, workspaceId]);

  const handlePatChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPat(e.target.value);
    if (errors.pat) setErrors((es) => ({ ...es, pat: undefined }));
  };

  const handleOrganizationChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ): void => {
    setOrganization(e.target.value);
    if (errors.organization)
      setErrors((es) => ({ ...es, organization: undefined }));
  };

  /** Reads the clipboard into the PAT field on an explicit user gesture. */
  const handlePasteFromClipboard = async (): Promise<void> => {
    if (action.blocked()) return;
    setPasteError(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setPat(text.trim());
      if (errors.pat) setErrors((es) => ({ ...es, pat: undefined }));
    } catch {
      setPasteError('Clipboard access denied. Paste manually with Cmd/Ctrl+V.');
    }
  };

  /** Pins the picked org on the fresh OAuth connection. */
  const handleSaveOauthOrganization = async (
    e: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (action.blocked()) return;
    if (organization.trim().length === 0) {
      setErrors({ organization: 'Organization is required' });
      if (explainUnavailableActions)
        notify.error(
          'Select or enter the Azure DevOps organization before saving.',
        );
      return;
    }
    const ticket = action.begin();
    if (!ticket) return;
    setSubmitting(true);
    setErrors({});
    try {
      await api.setAzureDevOpsOrganization(organization.trim(), workspaceId);
      action.complete(ticket, onSaved);
    } catch (err) {
      if (!action.isCurrent(ticket)) return;
      const message =
        err instanceof Error ? err.message : 'Failed to set the organization';
      setErrors({ submit: message });
    } finally {
      if (action.isCurrent(ticket)) setSubmitting(false);
      action.end(ticket);
    }
  };

  /** Token screen → org screen: discover the organizations the PAT can reach. */
  const handleContinue = async (
    e: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (action.blocked()) return;
    const token = pat.trim();
    if (token.length < 8) {
      setErrors({ pat: 'Token looks too short' });
      if (explainUnavailableActions)
        notify.error('Enter a valid personal access token before connecting.');
      return;
    }
    const ticket = action.begin();
    if (!ticket) return;
    setDiscovering(true);
    setErrors({});
    try {
      const found = await api.listAzureDevOpsOrganizations(token);
      if (!action.isCurrent(ticket)) return;
      setOrganizations(found);
      setOrganization(found?.[0] ?? '');
      setStep('org');
    } catch (err) {
      if (!action.isCurrent(ticket)) return;
      const message =
        err instanceof Error ? err.message : 'Failed to check the token';
      setErrors({ submit: message });
    } finally {
      if (action.isCurrent(ticket)) setDiscovering(false);
      action.end(ticket);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (action.blocked()) return;
    if (organization.trim().length === 0) {
      setErrors({ organization: 'Organization is required' });
      if (explainUnavailableActions)
        notify.error(
          'Select or enter the Azure DevOps organization before saving.',
        );
      return;
    }
    const ticket = action.begin();
    if (!ticket) return;
    setSubmitting(true);
    setErrors({});
    try {
      await api.saveAzureDevOpsCredentials(
        { pat: pat.trim(), organization: organization.trim() },
        workspaceId,
        details,
      );
      action.complete(ticket, onSaved);
    } catch (err) {
      if (!action.isCurrent(ticket)) return;
      const message =
        err instanceof Error ? err.message : 'Failed to save credentials';
      setErrors({ submit: message });
    } finally {
      if (action.isCurrent(ticket)) setSubmitting(false);
      action.end(ticket);
    }
  };

  if (step === 'oauth-org') {
    return (
      <form
        onSubmit={handleSaveOauthOrganization}
        className="mt-3 flex flex-col gap-3"
        noValidate
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ado-oauth-organization" className="text-xs">
            Organization
          </Label>
          {organizations && organizations.length > 0 ? (
            explainUnavailableActions ? (
              <ConnectionChoiceSelect
                id="ado-oauth-organization"
                value={organization}
                options={organizations.map((name) => ({
                  value: name,
                  label: name,
                }))}
                blocked={action.blocked}
                onValueChange={(organization) => {
                  setOrganization(organization);
                  if (errors.organization)
                    setErrors((current) => ({
                      ...current,
                      organization: undefined,
                    }));
                }}
              />
            ) : (
              <select
                id="ado-oauth-organization"
                value={organization}
                onChange={handleOrganizationChange}
                disabled={!explainUnavailableActions && submitting}
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-elev-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
              >
                {organizations.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            )
          ) : (
            <Input
              id="ado-oauth-organization"
              type="text"
              autoComplete="organization"
              spellCheck={false}
              placeholder="your-org"
              value={organization}
              onChange={handleOrganizationChange}
              disabled={submitting}
              aria-invalid={Boolean(errors.organization)}
            />
          )}
          {errors.organization ? (
            <p className="text-xs text-destructive">{errors.organization}</p>
          ) : organizations && organizations.length > 0 ? (
            <p className="text-[11px] leading-snug text-muted-foreground">
              You&apos;re connected. Pick the organization the assistant should
              work in.
            </p>
          ) : (
            <p className="text-[11px] leading-snug text-muted-foreground">
              You&apos;re connected. Type the organization name (the{' '}
              <span className="font-medium">your-org</span> part of
              dev.azure.com/your-org) the assistant should work in.
            </p>
          )}
        </div>
        {errors.submit && (
          <p className="text-xs text-destructive">{errors.submit}</p>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button
            type="submit"
            size="sm"
            disabled={!explainUnavailableActions && submitting}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    );
  }

  if (step === 'token') {
    return (
      <form
        onSubmit={handleContinue}
        className="mt-3 flex flex-col gap-3"
        noValidate
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ado-pat" className="text-xs">
            Personal Access Token
          </Label>
          <div className="flex items-stretch gap-1.5">
            <div className="relative flex-1">
              <Input
                id="ado-pat"
                type={showPat ? 'text' : 'password'}
                autoComplete="new-password"
                spellCheck={false}
                placeholder="Azure DevOps personal access token"
                value={pat}
                onChange={handlePatChange}
                disabled={discovering}
                aria-invalid={Boolean(errors.pat)}
                aria-describedby={errors.pat ? 'ado-pat-err' : 'ado-pat-help'}
                className="pr-8"
              />
              <button
                type="button"
                onClick={() => setShowPat((s) => !s)}
                aria-label={showPat ? 'Hide token' : 'Show token'}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
              >
                {showPat ? (
                  <EyeOffIcon
                    className="size-3.5"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                ) : (
                  <EyeIcon
                    className="size-3.5"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                )}
              </button>
            </div>
            <button
              type="button"
              onClick={() => void handlePasteFromClipboard()}
              disabled={!explainUnavailableActions && discovering}
              aria-label="Paste from clipboard"
              className={cn(
                'inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20',
              )}
            >
              <ClipboardIcon
                className="size-3.5"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              Paste
            </button>
          </div>
          {pasteError && (
            <p className="text-[11px] text-destructive">{pasteError}</p>
          )}
          {errors.pat ? (
            <p id="ado-pat-err" className="text-xs text-destructive">
              {errors.pat}
            </p>
          ) : (
            <p
              id="ado-pat-help"
              className="text-[11px] leading-snug text-muted-foreground"
            >
              In Azure DevOps, open{' '}
              <span className="font-medium">
                User settings → Personal access tokens
              </span>{' '}
              and create a token with{' '}
              <span className="font-medium">Work Items (Read &amp; write)</span>{' '}
              and{' '}
              <span className="font-medium">
                Test Management (Read &amp; write)
              </span>{' '}
              scopes. To pick the organization from a list on the next step,
              also set Organization to{' '}
              <span className="font-medium">All accessible organizations</span>{' '}
              and add the{' '}
              <span className="font-medium">User Profile (Read)</span> scope.
            </p>
          )}
        </div>
        {errors.submit && (
          <p className="text-xs text-destructive">{errors.submit}</p>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => action.cancel(onCancel)}
            disabled={!explainUnavailableActions && discovering}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={!explainUnavailableActions && discovering}
          >
            {discovering ? 'Checking…' : 'Continue'}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-3"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ado-organization" className="text-xs">
          Organization
        </Label>
        {organizations && organizations.length > 0 ? (
          explainUnavailableActions ? (
            <ConnectionChoiceSelect
              id="ado-organization"
              value={organization}
              options={organizations.map((name) => ({
                value: name,
                label: name,
              }))}
              blocked={action.blocked}
              onValueChange={(organization) => {
                setOrganization(organization);
                if (errors.organization)
                  setErrors((current) => ({
                    ...current,
                    organization: undefined,
                  }));
              }}
            />
          ) : (
            <select
              id="ado-organization"
              value={organization}
              onChange={handleOrganizationChange}
              disabled={!explainUnavailableActions && submitting}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-elev-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
            >
              {organizations.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          )
        ) : (
          <Input
            id="ado-organization"
            type="text"
            autoComplete="organization"
            spellCheck={false}
            placeholder="your-org"
            value={organization}
            onChange={handleOrganizationChange}
            disabled={submitting}
            aria-invalid={Boolean(errors.organization)}
            aria-describedby={
              errors.organization
                ? 'ado-organization-err'
                : 'ado-organization-help'
            }
          />
        )}
        {errors.organization ? (
          <p id="ado-organization-err" className="text-xs text-destructive">
            {errors.organization}
          </p>
        ) : organizations && organizations.length > 0 ? (
          <p className="text-[11px] leading-snug text-muted-foreground">
            Organizations this token can access.
          </p>
        ) : (
          <p
            id="ado-organization-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            Type the organization name (the{' '}
            <span className="font-medium">your-org</span> part of
            dev.azure.com/your-org). Azure DevOps only lists organizations for
            tokens created with Organization set to{' '}
            <span className="font-medium">All accessible organizations</span>{' '}
            plus the <span className="font-medium">User Profile (Read)</span>{' '}
            scope — this token isn&apos;t one, so pick the organization by name.
          </p>
        )}
      </div>
      {errors.submit && (
        <p className="text-xs text-destructive">{errors.submit}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            if (!action.blocked()) setStep('token');
          }}
          disabled={!explainUnavailableActions && submitting}
        >
          Back
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!explainUnavailableActions && submitting}
        >
          {submitting ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}
